import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, MessageSquare, Mail, CalendarX, Plus, X, Star, CalendarClock, KeyRound, BarChart3, CheckSquare, Bell, QrCode, Smartphone, Timer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { subscribeAdminPush, unsubscribeAdminPush } from "../../lib/push";
import { isChatSoundMuted, setChatSoundMuted } from "../../lib/chatSound";

const fmtHours = (h) => {
  if (h % 24 === 0) { const d = h / 24; return d === 1 ? "1 dag" : `${d} dagen`; }
  return h === 1 ? "1 uur" : `${h} uur`;
};

const MONTHS_NL = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const fmtMonth = (m) => {
  const [y, mm] = (m || "").split("-");
  const idx = Number(mm) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS_NL[idx]} ${y}` : m;
};
const fmtEuro = (n) => `€ ${(Number(n) || 0).toFixed(2).replace(".", ",")}`;

export default function Settings() {
  const [s, setS] = useState(null);
  const [newHour, setNewHour] = useState("");
  const [pinSet, setPinSet] = useState(false);
  const [pin, setPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  const [stats, setStats] = useState(null);
  const [soundOn, setSoundOn] = useState(!isChatSoundMuted());
  const [qrOpen, setQrOpen] = useState(false);
  const [restOpen, setRestOpen] = useState(false);
  const [restSaving, setRestSaving] = useState(false);
  const [restBands, setRestBands] = useState({ upto_15: 5, upto_30: 10, upto_60: 15, over_60: 20 });
  const plannerUrl = `${window.location.origin}/planner`;

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { if (s?.rest_bands) setRestBands(s.rest_bands); }, [s]);
  useEffect(() => { api.get("/auth/me").then((r) => setPinSet(!!r.data.pin_set)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/message-stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const savePin = async () => {
    if (!/^\d{4,6}$/.test(pin)) { toast.error("De pincode moet uit 4 tot 6 cijfers bestaan."); return; }
    setSavingPin(true);
    try {
      await api.post("/auth/set-pin", { pin });
      setPinSet(true); setPin("");
      toast.success("Pincode opgeslagen. Je kunt hiermee inloggen.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPin(false); }
  };

  const removePin = async () => {
    if (!window.confirm("Pincode-inloggen uitschakelen?")) return;
    try {
      await api.delete("/auth/pin");
      setPinSet(false); setPin("");
      toast.success("Pincode verwijderd.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addHour = () => {
    const n = Number(newHour);
    if (!n || n < 1) { toast.error("Vul een geldig aantal uur in."); return; }
    const list = s.reminder_hours_list || [];
    if (list.includes(n)) { toast.error("Dit moment staat er al in."); return; }
    setS({ ...s, reminder_hours_list: [...list, n].sort((a, b) => b - a) });
    setNewHour("");
  };
  const removeHour = (h) => setS({ ...s, reminder_hours_list: (s.reminder_hours_list || []).filter((x) => x !== h) });

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        reminder_hours_list: (s.reminder_hours_list || []).map(Number),
        reminder_email_enabled: s.reminder_email_enabled,
        reminder_sms_enabled: s.reminder_sms_enabled,
        confirmation_email_enabled: s.confirmation_email_enabled,
        winback_subject: s.winback_subject || "",
        winback_body: s.winback_body || "",
        cancel_hours_before: Number(s.cancel_hours_before),
        google_review_url: s.google_review_url || "",
        booking_enabled: s.booking_enabled !== false,
        booking_disabled_message: s.booking_disabled_message || "",
        sms_price: Number(s.sms_price) || 0,
        email_price: Number(s.email_price) || 0,
        twilio_number_price: Number(s.twilio_number_price) || 0,
        site_monthly_cost: Number(s.site_monthly_cost) || 0,
        cost_report_email: s.cost_report_email || "",
        public_url: (s.public_url || "").trim(),
        todo_reminder_days: Number(s.todo_reminder_days) || 0,
        todo_reminder_email: s.todo_reminder_email !== false,
        todo_reminder_push: !!s.todo_reminder_push,
        chat_notify_email: s.chat_notify_email !== false,
        chat_notify_push: !!s.chat_notify_push,
        morning_digest_enabled: s.morning_digest_enabled !== false,
        auto_refresh_min: Number(s.auto_refresh_min) || 0,
        show_weekend: s.show_weekend !== false,
        daily_max_load: Number(s.daily_max_load) || 8,
        autoblock_load_enabled: !!s.autoblock_load_enabled,
        autoblock_load_pct: Number(s.autoblock_load_pct) || 0,
        daily_ideal_min: Number(s.daily_ideal_min) || 3,
        daily_ideal_max: Number(s.daily_ideal_max) || 6,
        daily_max_load_by_day: (s.daily_max_load_by_day || [0, 0, 0, 0, 0, 0, 0]).map(Number),
        private_margin_min: Number(s.private_margin_min) || 0,
        assistant_private_worktime: s.assistant_private_worktime !== false,
        assistant_closed_days: s.assistant_closed_days || "off",
        planner_extended_hours: !!s.planner_extended_hours,
        planner_early_hour: Number(s.planner_early_hour) || 7,
        planner_late_hour: Number(s.planner_late_hour) || 21,
        planner_extended_color: s.planner_extended_color !== false,
        planner_extended_color_value: s.planner_extended_color_value || "#ECEAF6",
      });
      toast.success("Instellingen opgeslagen.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendTestReport = async () => {
    try {
      await save();
      const r = await api.post("/admin/cost-report/test");
      toast.success(`Testoverzicht wordt verstuurd naar ${r.data.to}.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!s) return null;

  const smsPrice = Number(s.sms_price) || 0;
  const emailPrice = Number(s.email_price) || 0;

  const togglePush = async (v) => {
    if (v) {
      try {
        await subscribeAdminPush(api);
        setS({ ...s, todo_reminder_push: true });
        await api.put("/admin/settings", { todo_reminder_push: true });
        toast.success("Push-meldingen ingeschakeld op dit apparaat.");
      } catch (e) { toast.error(e.message || "Kon push niet inschakelen."); }
    } else {
      try { await unsubscribeAdminPush(api); } catch (e) {}
      setS({ ...s, todo_reminder_push: false });
      await api.put("/admin/settings", { todo_reminder_push: false });
      toast.success("Push-meldingen uitgeschakeld.");
    }
  };

  const toggleChatPush = async (v) => {
    if (v) {
      try {
        await subscribeAdminPush(api);
        setS({ ...s, chat_notify_push: true });
        await api.put("/admin/settings", { chat_notify_push: true });
        toast.success("Chat push-meldingen ingeschakeld op dit apparaat.");
      } catch (e) { toast.error(e.message || "Kon push niet inschakelen."); }
    } else {
      setS({ ...s, chat_notify_push: false });
      await api.put("/admin/settings", { chat_notify_push: false });
      toast.success("Chat push-meldingen uitgeschakeld.");
    }
  };

  const sendTestPush = async () => {
    try {
      const r = await api.post("/admin/push/test");
      toast.success(`Testmelding verstuurd naar ${r.data.sent}/${r.data.devices} apparaat/apparaten. Check je meldingen.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const Row = ({ title, desc, children }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 py-4 border-b border-[#F2EBE1] last:border-0">
      <div className="min-w-0"><div className="font-medium">{title}</div><div className="text-sm text-ink-muted">{desc}</div></div>
      <div className="shrink-0 self-start sm:self-auto">{children}</div>
    </div>
  );

  const applyRest = async () => {
    setRestSaving(true);
    try {
      const body = {
        upto_15: Number(restBands.upto_15) || 0,
        upto_30: Number(restBands.upto_30) || 0,
        upto_60: Number(restBands.upto_60) || 0,
        over_60: Number(restBands.over_60) || 0,
      };
      const r = await api.post("/admin/treatments/apply-rest", body);
      setS((prev) => prev ? { ...prev, rest_bands: body } : prev);
      toast.success(`Rusttijd toegepast op ${r.data.updated} behandeling${r.data.updated === 1 ? "" : "en"}.`);
      setRestOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRestSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-medium tracking-tight mb-1">Instellingen</h1>
      <p className="text-ink-muted mb-8">Beheer bevestigingen en herinneringen.</p>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brand" strokeWidth={1.5} /> Online reserveren</h2>
        <Row title="Reserveren mogelijk" desc="Zet uit om online reserveren tijdelijk te sluiten. De reserveringswizard op de site wordt dan vervangen door je bericht hieronder — je agenda-instellingen blijven ongemoeid.">
          <Switch data-testid="toggle-booking" checked={s.booking_enabled !== false}
            onCheckedChange={(v) => setS({ ...s, booking_enabled: v })} />
        </Row>
        {s.booking_enabled === false && (
          <div className="pt-4">
            <Label className="text-sm text-ink-muted mb-1.5 block">Bericht bij gesloten reserveren</Label>
            <Textarea data-testid="booking-disabled-message" rows={3} value={s.booking_disabled_message || ""}
              onChange={(e) => setS({ ...s, booking_disabled_message: e.target.value })}
              placeholder="Nu even niet mogelijk, binnenkort weer..." className="rounded-xl border-[#E8E2D9]" />
          </div>
        )}
        <Row title="Website-adres" desc="Domein voor links in e-mails en SMS (annuleren, verzetten, agenda).">
          <Input data-testid="public-url-input" type="url"
            value={s.public_url ?? ""}
            onChange={(e) => setS({ ...s, public_url: e.target.value })}
            placeholder="https://nellekeholt.nl"
            className="w-full sm:w-64 rounded-xl border-[#E8E2D9]" />
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6" data-testid="rest-bands-card">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Timer className="w-5 h-5 text-brand" strokeWidth={1.5} /> Rusttijd behandelingen</h2>
        <Row title="Rusttijd automatisch instellen" desc="Stel in één keer de rusttijd (marge na een behandeling) in voor al je behandelingen, op basis van hun duur.">
          <Button type="button" onClick={() => setRestOpen(true)} data-testid="open-rest-wizard-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
            <Timer className="w-4 h-4 mr-2" /> Rusttijd instellen
          </Button>
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-brand" strokeWidth={1.5} /> Bevestiging</h2>
        <Row title="Bevestigingsmail versturen" desc="Stuur een e-mail met .ics agenda-uitnodiging zodra je een afspraak inplant.">
          <Switch data-testid="toggle-confirmation" checked={s.confirmation_email_enabled}
            onCheckedChange={(v) => setS({ ...s, confirmation_email_enabled: v })} />
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-brand" strokeWidth={1.5} /> Win-back bericht (slapende klanten)</h2>
        <p className="text-sm text-ink-muted mb-4">Dit bericht wordt gebruikt bij "Slapend benaderen" op de Klantenpagina. Het gaat altijd via e-mail in jullie huisstijl.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Onderwerp</Label>
            <Input data-testid="winback-setting-subject" value={s.winback_subject ?? ""}
              onChange={(e) => setS({ ...s, winback_subject: e.target.value })} className="rounded-xl border-[#E8E2D9]" />
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Bericht</Label>
            <Textarea data-testid="winback-setting-body" rows={7} value={s.winback_body ?? ""}
              onChange={(e) => setS({ ...s, winback_body: e.target.value })} className="rounded-xl border-[#E8E2D9]" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-brand" strokeWidth={1.5} /> Herinneringen</h2>
        <Row title="Herinnering per e-mail" desc="Verstuur automatisch een herinneringsmail voor de afspraak.">
          <Switch data-testid="toggle-reminder-email" checked={s.reminder_email_enabled}
            onCheckedChange={(v) => setS({ ...s, reminder_email_enabled: v })} />
        </Row>
        <Row title="Herinnering per SMS" desc="Verstuur automatisch een SMS (via Twilio) voor de afspraak.">
          <Switch data-testid="toggle-reminder-sms" checked={s.reminder_sms_enabled}
            onCheckedChange={(v) => setS({ ...s, reminder_sms_enabled: v })} />
        </Row>
        <Row title="Herinneringsmomenten" desc="Voeg één of meerdere momenten toe. Bijv. 24 uur én 2 uur van tevoren.">
          <div className="flex flex-col items-start sm:items-end gap-2">
            <div className="flex flex-wrap gap-1.5 max-w-full sm:max-w-[260px] sm:justify-end">
              {(s.reminder_hours_list || []).length === 0 && (
                <span className="text-xs text-ink-muted">Nog geen momenten</span>
              )}
              {(s.reminder_hours_list || []).map((h) => (
                <span key={h} data-testid={`reminder-chip-${h}`}
                  className="inline-flex items-center gap-1 bg-brand-light text-ink rounded-full pl-3 pr-1.5 py-1 text-sm">
                  {fmtHours(h)}
                  <button onClick={() => removeHour(h)} data-testid={`remove-reminder-${h}`}
                    className="rounded-full hover:bg-white/60 p-0.5"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <Input data-testid="reminder-hours-input" type="number" min="1" value={newHour}
                onChange={(e) => setNewHour(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHour(); } }}
                placeholder="uur" className="w-20 rounded-xl border-[#E8E2D9] text-center" />
              <Button type="button" size="icon" variant="outline" onClick={addHour} data-testid="add-reminder-btn"
                className="rounded-full shrink-0"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6" data-testid="chat-notify-card">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Bell className="w-5 h-5 text-brand" strokeWidth={1.5} /> Chatmeldingen</h2>
        <p className="text-sm text-ink-muted mb-2">Ontvang een melding zodra een klant een nieuw bericht stuurt via de website-chat.</p>
        <Row title="Melding per e-mail" desc="Stuur een e-mail naar de beheerder bij een nieuw chatbericht.">
          <Switch data-testid="toggle-chat-email" checked={s.chat_notify_email !== false}
            onCheckedChange={(v) => setS({ ...s, chat_notify_email: v })} />
        </Row>
        <Row title="Push-melding" desc="Ontvang een pushmelding op dit apparaat bij een nieuw chatbericht.">
          <Switch data-testid="toggle-chat-push" checked={!!s.chat_notify_push} onCheckedChange={toggleChatPush} />
        </Row>
        <Row title="Geluid bij nieuw bericht" desc="Speel een kort geluidje af in het beheer zodra een nieuw chatbericht binnenkomt (instelling per apparaat).">
          <Switch data-testid="toggle-chat-sound" checked={soundOn}
            onCheckedChange={(v) => { setSoundOn(v); setChatSoundMuted(!v); toast.success(v ? "Chatgeluid aan." : "Chatgeluid gedempt."); }} />
        </Row>
        {!!s.chat_notify_push && (
          <Button type="button" variant="outline" onClick={sendTestPush} data-testid="test-push-chat-btn" className="mt-3 rounded-full">
            <Bell className="w-4 h-4 mr-2" /> Stuur testmelding
          </Button>
        )}
        <p className="text-xs text-ink-muted mt-2">Om spam te voorkomen ontvang je per gesprek maximaal één melding per 3 minuten.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6" data-testid="todo-reminder-card">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-brand" strokeWidth={1.5} /> To-Do herinneringen</h2>
        <Row title="Aantal dagen vóór due" desc="Stuur een herinnering zoveel dagen vóór de due-datum van een to-do, als die nog niet is afgevinkt.">
          <div className="flex items-center gap-2">
            <Input data-testid="todo-reminder-days" type="number" min="0" value={s.todo_reminder_days ?? 1}
              onChange={(e) => setS({ ...s, todo_reminder_days: e.target.value })}
              className="w-24 rounded-xl border-[#E8E2D9] text-center" />
            <span className="text-sm text-ink-muted">dag(en)</span>
          </div>
        </Row>
        <Row title="Herinnering per e-mail" desc="Ontvang de to-do herinnering op je e-mail.">
          <Switch data-testid="toggle-todo-email" checked={s.todo_reminder_email !== false}
            onCheckedChange={(v) => setS({ ...s, todo_reminder_email: v })} />
        </Row>
        <Row title="Push-melding" desc="Ontvang een pushmelding op dit apparaat (browser/telefoon). Vraagt eenmalig toestemming.">
          <Switch data-testid="toggle-todo-push" checked={!!s.todo_reminder_push}
            onCheckedChange={togglePush} />
        </Row>
        {!!s.todo_reminder_push && (
          <Button type="button" variant="outline" onClick={sendTestPush} data-testid="test-push-todo-btn" className="mt-3 rounded-full">
            <Bell className="w-4 h-4 mr-2" /> Stuur testmelding
          </Button>
        )}
        <p className="text-xs text-ink-muted mt-2 flex items-start gap-1.5"><Bell className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Push werkt per apparaat: schakel het in op elk apparaat waarop je meldingen wilt ontvangen.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6" data-testid="message-stats-card">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-brand" strokeWidth={1.5} /> Verzonden berichten</h2>
        <p className="text-sm text-ink-muted mb-4">Aantal verstuurde e-mails en SMS'jes per maand (bevestigingen, herinneringen, nieuws en reviews).</p>
        {stats && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl bg-brand-light p-4" data-testid="stat-email-current">
                <div className="flex items-center gap-1.5 text-ink-muted text-sm"><Mail className="w-4 h-4" /> E-mails deze maand</div>
                <div className="text-3xl font-medium mt-1">{stats.current?.email ?? 0}</div>
                <div className="text-sm text-ink-muted mt-0.5" data-testid="stat-email-cost-current">± {fmtEuro((stats.current?.email ?? 0) * emailPrice)} kosten</div>
              </div>
              <div className="rounded-xl bg-brand-light p-4" data-testid="stat-sms-current">
                <div className="flex items-center gap-1.5 text-ink-muted text-sm"><MessageSquare className="w-4 h-4" /> SMS deze maand</div>
                <div className="text-3xl font-medium mt-1">{stats.current?.sms ?? 0}</div>
                <div className="text-sm text-ink-muted mt-0.5" data-testid="stat-sms-cost-current">± {fmtEuro((stats.current?.sms ?? 0) * smsPrice)} kosten</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5 py-3 px-4 rounded-xl border border-[#F2EBE1] bg-[#FAF7F2]">
              <div>
                <div className="font-medium text-sm">SMS-tarief per bericht</div>
                <div className="text-xs text-ink-muted">Wordt gebruikt om de geschatte SMS-kosten te berekenen.</div>
              </div>
              <div className="flex items-center gap-1.5 self-start sm:self-auto">
                <span className="text-ink-muted">€</span>
                <Input data-testid="sms-price-input" type="number" step="0.01" min="0"
                  value={s.sms_price ?? ""}
                  onChange={(e) => setS({ ...s, sms_price: e.target.value })}
                  className="w-24 rounded-xl border-[#E8E2D9] text-center" />
              </div>
            </div>
            {stats.months?.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-[#F2EBE1]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FAF7F2] text-ink-muted text-left">
                      <th className="py-2.5 px-3 sm:px-4 font-medium">Maand</th>
                      <th className="py-2.5 px-2 sm:px-4 font-medium text-right">E-mail</th>
                      <th className="py-2.5 px-2 sm:px-4 font-medium text-right">SMS</th>
                      <th className="py-2.5 px-3 sm:px-4 font-medium text-right">SMS-kosten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.months.map((m) => (
                      <tr key={m.month} className="border-t border-[#F2EBE1]" data-testid={`stat-row-${m.month}`}>
                        <td className="py-2.5 px-3 sm:px-4 capitalize">{fmtMonth(m.month)}</td>
                        <td className="py-2.5 px-2 sm:px-4 text-right tabular-nums">{m.email}</td>
                        <td className="py-2.5 px-2 sm:px-4 text-right tabular-nums">{m.sms}</td>
                        <td className="py-2.5 px-3 sm:px-4 text-right tabular-nums">{fmtEuro(m.sms * smsPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">Nog geen berichten verstuurd.</p>
            )}
            <p className="text-xs text-ink-muted mt-3">SMS-kosten zijn een schatting op basis van het ingestelde tarief. Vergeet niet op te slaan na het wijzigen van het tarief.</p>
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6" data-testid="cost-report-card">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-brand" strokeWidth={1.5} /> Maandelijks kostenoverzicht</h2>
        <p className="text-sm text-ink-muted mb-4">Op de 1e van elke maand wordt automatisch een kostenoverzicht van de afgelopen maand gemaild. Credits worden 1:1 omgerekend naar euro.</p>

        <Row title="Ontvangers overzicht" desc="E-mailadres(sen) die het maandoverzicht ontvangen. Meerdere? Scheid ze met een komma.">
          <Input data-testid="cost-report-email-input" type="text"
            value={s.cost_report_email ?? ""}
            onChange={(e) => setS({ ...s, cost_report_email: e.target.value })}
            placeholder="naam@voorbeeld.nl, tweede@voorbeeld.nl"
            className="w-full sm:w-80 rounded-xl border-[#E8E2D9]" />
        </Row>
        <Row title="E-mailtarief per bericht" desc="Bedrag per verstuurde e-mail (Resend).">
          <div className="flex items-center gap-1.5">
            <span className="text-ink-muted">€</span>
            <Input data-testid="email-price-input" type="number" step="0.001" min="0"
              value={s.email_price ?? ""}
              onChange={(e) => setS({ ...s, email_price: e.target.value })}
              className="w-28 rounded-xl border-[#E8E2D9] text-center" />
          </div>
        </Row>
        <Row title="Twilio telefoonnummer (maandhuur)" desc="Vaste maandelijkse kosten voor het SMS-nummer.">
          <div className="flex items-center gap-1.5">
            <span className="text-ink-muted">€</span>
            <Input data-testid="twilio-number-price-input" type="number" step="0.01" min="0"
              value={s.twilio_number_price ?? ""}
              onChange={(e) => setS({ ...s, twilio_number_price: e.target.value })}
              className="w-28 rounded-xl border-[#E8E2D9] text-center" />
          </div>
        </Row>
        <Row title="Website actief houden & ontwikkeling" desc="Vast maandbedrag om de site te hosten en door te ontwikkelen.">
          <div className="flex items-center gap-1.5">
            <span className="text-ink-muted">€</span>
            <Input data-testid="site-cost-input" type="number" step="0.01" min="0"
              value={s.site_monthly_cost ?? ""}
              onChange={(e) => setS({ ...s, site_monthly_cost: e.target.value })}
              className="w-28 rounded-xl border-[#E8E2D9] text-center" />
          </div>
        </Row>

        <div className="mt-4 rounded-xl bg-brand-light p-4" data-testid="cost-report-preview">
          <div className="text-sm text-ink-muted mb-2">Voorbeeld op basis van deze maand:</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>SMS ({stats?.current?.sms ?? 0})</span><span className="tabular-nums">{fmtEuro((stats?.current?.sms ?? 0) * smsPrice)}</span></div>
            <div className="flex justify-between"><span>E-mail ({stats?.current?.email ?? 0})</span><span className="tabular-nums">{fmtEuro((stats?.current?.email ?? 0) * emailPrice)}</span></div>
            <div className="flex justify-between"><span>Twilio nummer</span><span className="tabular-nums">{fmtEuro(Number(s.twilio_number_price) || 0)}</span></div>
            <div className="flex justify-between"><span>Website & ontwikkeling</span><span className="tabular-nums">{fmtEuro(Number(s.site_monthly_cost) || 0)}</span></div>
            <div className="flex justify-between font-medium border-t border-[#E8D9D9] pt-1 mt-1">
              <span>Totaal</span>
              <span className="tabular-nums" data-testid="cost-report-total">{fmtEuro((stats?.current?.sms ?? 0) * smsPrice + (stats?.current?.email ?? 0) * emailPrice + (Number(s.twilio_number_price) || 0) + (Number(s.site_monthly_cost) || 0))}</span>
            </div>
          </div>
        </div>

        <Button data-testid="send-test-report-btn" type="button" variant="outline" onClick={sendTestReport}
          className="mt-4 rounded-full">
          <Mail className="w-4 h-4 mr-2" /> Opslaan &amp; testoverzicht sturen
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><CalendarX className="w-5 h-5 text-brand" strokeWidth={1.5} /> Annuleren door klant</h2>
        <Row title="Zelf annuleren toegestaan tot" desc="Klanten kunnen via de link in de e-mail/SMS zelf annuleren, tot dit aantal uur voor de afspraak.">
          <div className="flex items-center gap-2">
            <Input data-testid="cancel-hours" type="number" min="0" value={s.cancel_hours_before}
              onChange={(e) => setS({ ...s, cancel_hours_before: e.target.value })}
              className="w-24 rounded-xl border-[#E8E2D9] text-center" />
            <span className="text-sm text-ink-muted">uur</span>
          </div>
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Star className="w-5 h-5 text-brand" strokeWidth={1.5} /> Reviews</h2>
        <Row title="Google-reviewlink" desc="De link die klanten openen als je vanuit een klantprofiel om een review vraagt. Plak hier je Google 'schrijf een review'-link.">
          <Input data-testid="google-review-url" value={s.google_review_url || ""}
            onChange={(e) => setS({ ...s, google_review_url: e.target.value })}
            placeholder="https://..." className="w-full max-w-[280px] rounded-xl border-[#E8E2D9]" />
        </Row>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><KeyRound className="w-5 h-5 text-brand" strokeWidth={1.5} /> Inloggen met pincode</h2>
        <Row title={pinSet ? "Pincode is ingesteld" : "Pincode instellen"}
          desc={pinSet ? "Je kunt inloggen met je pincode. Vul een nieuwe code in om te wijzigen." : "Stel een pincode (4–6 cijfers) in om snel in te loggen naast e-mail + wachtwoord."}>
          <div className="flex items-center gap-2">
            <Input data-testid="settings-pin-input" type="password" inputMode="numeric" maxLength={6}
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder={pinSet ? "nieuwe code" : "1234"} className="w-28 rounded-xl border-[#E8E2D9] text-center tracking-widest" />
            <Button type="button" onClick={savePin} disabled={savingPin} data-testid="save-pin-btn"
              className="rounded-full bg-brand hover:bg-brand-hover text-white">Opslaan</Button>
          </div>
        </Row>
        {pinSet && (
          <div className="pt-3">
            <button onClick={removePin} data-testid="remove-pin-btn" className="text-sm text-destructive hover:underline">Pincode-inloggen uitschakelen</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-brand" strokeWidth={1.5} /> Ochtendbericht</h2>
        <Row title="Dagoverzicht 's ochtends mailen" desc="Ontvang elke ochtend rond 07:00 een kort overzicht van je afspraken en open to-do's van die dag.">
          <Switch data-testid="morning-digest-toggle" checked={s.morning_digest_enabled !== false} onCheckedChange={(v) => setS({ ...s, morning_digest_enabled: v })} />
        </Row>
        <Row title="Scherm automatisch verversen" desc="Ververs het scherm in beheer én de Planner elke X minuten (0 = uit). Handig op een tablet die altijd aanstaat.">
          <div className="flex items-center gap-2">
            <Input data-testid="auto-refresh-input" type="number" min="0" step="1" value={s.auto_refresh_min ?? 5} onChange={(e) => setS({ ...s, auto_refresh_min: e.target.value })} className="w-24 rounded-xl border-[#E8E2D9]" />
            <span className="text-sm text-ink-muted">min</span>
          </div>
        </Row>
        <Row title="Weekend tonen in agenda" desc="Toon zaterdag en zondag in de weekweergave. Uit = alleen maandag t/m vrijdag.">
          <Switch data-testid="show-weekend-toggle" checked={s.show_weekend !== false} onCheckedChange={(v) => setS({ ...s, show_weekend: v })} />
        </Row>
        <Row title="Planningsassistent — max. belasting per dag" desc="Boven deze belasting stelt de assistent geen afspraken meer voor.">
          <Input data-testid="daily-max-load" type="number" min="1" max="20" value={s.daily_max_load ?? 8} onChange={(e) => setS({ ...s, daily_max_load: e.target.value })} className="w-24 rounded-xl border-[#E8E2D9] text-center" />
        </Row>
        <Row title="Max. belasting per weekdag (optioneel)" desc="0 = gebruik de standaard hierboven. Zet bv. maandag lager voor een rustigere start.">
          <div className="flex gap-1 flex-wrap">
            {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((lbl, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <span className="text-[10px] text-ink-muted mb-0.5">{lbl}</span>
                <Input data-testid={`max-load-day-${idx}`} type="number" min="0" max="20"
                  value={(s.daily_max_load_by_day || [0, 0, 0, 0, 0, 0, 0])[idx] ?? 0}
                  onChange={(e) => { const arr = [...(s.daily_max_load_by_day || [0, 0, 0, 0, 0, 0, 0])]; arr[idx] = Number(e.target.value) || 0; setS({ ...s, daily_max_load_by_day: arr }); }}
                  className="w-11 rounded-lg border-[#E8E2D9] text-center px-1" />
              </div>
            ))}
          </div>
        </Row>
        <Row title="Autoblock bij drukte" desc="Als een dag qua belasting (bijna) vol zit, sluit online reserveren voor die dag automatisch — ook al is er nog tijd vrij.">
          <Switch data-testid="autoblock-load-toggle" checked={!!s.autoblock_load_enabled} onCheckedChange={(v) => setS({ ...s, autoblock_load_enabled: v })} />
        </Row>
        {s.autoblock_load_enabled && (
          <Row title="Blokkeren vanaf X% boven maximum" desc="0% = blokkeren zodra de maximale belasting is bereikt. Bijv. 20% = pas blokkeren bij 20% boven het maximum.">
            <div className="flex items-center gap-1">
              <Input data-testid="autoblock-load-pct" type="number" min="0" max="200" step="5" value={s.autoblock_load_pct ?? 20} onChange={(e) => setS({ ...s, autoblock_load_pct: e.target.value })} className="w-20 rounded-xl border-[#E8E2D9] text-center" />
              <span className="text-sm text-ink-muted">%</span>
            </div>
          </Row>
        )}
        <Row title="Marge rond privé-afspraken (min)" desc="Extra reistijd/rust vóór en ná een privé-afspraak; de assistent plant dan niet vlak ertegenaan.">
          <Input data-testid="private-margin" type="number" min="0" max="120" step="5" value={s.private_margin_min ?? 0} onChange={(e) => setS({ ...s, private_margin_min: e.target.value })} className="w-24 rounded-xl border-[#E8E2D9] text-center" />
        </Row>
        <Row title="Assistent mag privé in werktijd plannen" desc="Aan: de assistent stelt privé-afspraken ook binnen openingstijden voor. Uit: alleen vóór/ná werktijd.">
          <Switch data-testid="assistant-private-worktime" checked={s.assistant_private_worktime !== false} onCheckedChange={(v) => setS({ ...s, assistant_private_worktime: v })} />
        </Row>
        <Row title="Assistent op gesloten dagen" desc="Mag de assistent ook op gesloten dagen (bv. je vrije dag) afspraken voorstellen — en zo ja, alleen privé of ook zakelijk?">
          <div className="flex gap-1">
            {[["off", "Uit"], ["private", "Alleen privé"], ["all", "Ook zakelijk"]].map(([v, l]) => (
              <button key={v} data-testid={`assistant-closed-${v}`} onClick={() => setS({ ...s, assistant_closed_days: v })}
                className={`rounded-lg px-2.5 py-1.5 text-xs border transition-colors ${(s.assistant_closed_days || "off") === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{l}</button>
            ))}
          </div>
        </Row>
        <Row title="Ideale belasting per dag" desc="Het bereik waarin je het liefst zit; hierbinnen krijgt een dag het label 'Ideaal'.">
          <div className="flex items-center gap-2">
            <Input data-testid="daily-ideal-min" type="number" min="0" max="20" value={s.daily_ideal_min ?? 3} onChange={(e) => setS({ ...s, daily_ideal_min: e.target.value })} className="w-20 rounded-xl border-[#E8E2D9] text-center" />
            <span className="text-sm text-ink-muted">t/m</span>
            <Input data-testid="daily-ideal-max" type="number" min="1" max="20" value={s.daily_ideal_max ?? 6} onChange={(e) => setS({ ...s, daily_ideal_max: e.target.value })} className="w-20 rounded-xl border-[#E8E2D9] text-center" />
          </div>
        </Row>
        <Row title="Uren vóór/ná werktijd tonen" desc="Toon in de agenda ook tijd vóór en ná je werktijd (handig voor privé-afspraken), met een aparte kleur. Uit = alleen 09:00–18:00.">
          <Switch data-testid="extended-hours-toggle" checked={!!s.planner_extended_hours} onCheckedChange={(v) => setS({ ...s, planner_extended_hours: v })} />
        </Row>
        {s.planner_extended_hours && (
          <>
            <Row title="Vroegste uur" desc="Hoe vroeg de agenda begint (0–9).">
              <div className="flex items-center gap-2">
                <Input data-testid="early-hour-input" type="number" min="0" max="9" value={s.planner_early_hour ?? 7}
                  onChange={(e) => setS({ ...s, planner_early_hour: e.target.value })}
                  className="w-24 rounded-xl border-[#E8E2D9] text-center" />
                <span className="text-sm text-ink-muted">uur</span>
              </div>
            </Row>
            <Row title="Laatste uur" desc="Tot hoe laat de agenda loopt (18–24).">
              <div className="flex items-center gap-2">
                <Input data-testid="late-hour-input" type="number" min="18" max="24" value={s.planner_late_hour ?? 21}
                  onChange={(e) => setS({ ...s, planner_late_hour: e.target.value })}
                  className="w-24 rounded-xl border-[#E8E2D9] text-center" />
                <span className="text-sm text-ink-muted">uur</span>
              </div>
            </Row>
            <Row title="Uren buiten werktijd met aparte kleur markeren" desc="Toon de tijd vóór/ná werktijd in een aparte kleur. Uit = gewone weergave, zonder markering.">
              <Switch data-testid="extended-color-toggle" checked={s.planner_extended_color !== false} onCheckedChange={(v) => setS({ ...s, planner_extended_color: v })} />
            </Row>
            {s.planner_extended_color !== false && (
              <Row title="Kleur buiten werktijd" desc="Kies de kleur voor de uren buiten werktijd in de agenda.">
                <div className="flex items-center gap-2">
                  {["#ECEAF6", "#E7F0E9", "#FBE9E4", "#E6EFF5", "#F6E9EF", "#F3EEDD"].map((c) => (
                    <button key={c} type="button" data-testid={`ext-color-${c.replace("#", "")}`} onClick={() => setS({ ...s, planner_extended_color_value: c })}
                      className="w-6 h-6 rounded-full border border-[#E8E2D9] transition-transform hover:scale-110"
                      style={{ backgroundColor: c, boxShadow: (s.planner_extended_color_value || "#ECEAF6") === c ? "0 0 0 2px #4A3F3F" : "none" }} />
                  ))}
                  <input type="color" data-testid="ext-color-input" value={s.planner_extended_color_value || "#ECEAF6"}
                    onChange={(e) => setS({ ...s, planner_extended_color_value: e.target.value })}
                    className="w-9 h-9 rounded-lg cursor-pointer border border-[#E8E2D9] bg-transparent p-0.5" title="Eigen kleur" />
                </div>
              </Row>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-2 flex items-center gap-2"><Smartphone className="w-5 h-5 text-brand" strokeWidth={1.5} /> NH Planner-app op je telefoon</h2>
        <Row title="Installeer de Planner-app" desc="De losse app met alleen je Agenda en To-Do's. Scan de QR-code met je telefoon of tablet en kies 'Toevoegen aan beginscherm'.">
          <Button type="button" onClick={() => setQrOpen(true)} data-testid="show-planner-qr-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
            <QrCode className="w-4 h-4 mr-2" /> Toon QR-code
          </Button>
        </Row>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="rounded-2xl max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center justify-center gap-2"><Smartphone className="w-5 h-5 text-brand" /> NH Planner installeren</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-1" data-testid="planner-qr">
            <div className="bg-white p-4 rounded-2xl border border-[#E8E2D9]">
              <QRCodeSVG value={plannerUrl} size={200} fgColor="#4A3F3F" bgColor="#ffffff" />
            </div>
            <ol className="text-sm text-ink-muted text-left list-decimal pl-5 space-y-1 self-start">
              <li>Scan deze code met de camera van je telefoon of tablet.</li>
              <li>Log in met je pincode of e-mail.</li>
              <li>Kies in je browser <span className="text-ink font-medium">'Toevoegen aan beginscherm'</span>.</li>
            </ol>
            <div className="w-full">
              <div className="text-xs text-ink-muted mb-1">Of open deze link:</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={plannerUrl} className="rounded-xl border-[#E8E2D9] text-xs" data-testid="planner-qr-url" />
                <Button type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(plannerUrl); toast.success("Link gekopieerd."); }} className="rounded-full border-[#E8E2D9] shrink-0">Kopieer</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={restOpen} onOpenChange={setRestOpen}>
        <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto no-scrollbar" aria-describedby={undefined} data-testid="rest-wizard">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Timer className="w-5 h-5 text-brand" /> Rusttijd per duur</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-muted">Bepaal hoeveel rusttijd (in minuten) na een behandeling wordt gereserveerd, op basis van de duur. Dit overschrijft de huidige rusttijd van álle behandelingen.</p>
          <div className="space-y-3 mt-2">
            {[
              { key: "upto_15", label: "Behandelingen t/m 15 min" },
              { key: "upto_30", label: "Behandelingen 15 t/m 30 min" },
              { key: "upto_60", label: "Behandelingen 30 t/m 60 min" },
              { key: "over_60", label: "Behandelingen langer dan 60 min" },
            ].map((b) => (
              <div key={b.key} className="flex items-center justify-between gap-3 rounded-xl border border-[#E8E2D9] px-4 py-3">
                <Label className="text-sm">{b.label}</Label>
                <div className="flex items-center gap-2 shrink-0">
                  <Input data-testid={`rest-band-${b.key}`} type="number" min="0" step="5" value={restBands[b.key]}
                    onChange={(e) => setRestBands({ ...restBands, [b.key]: e.target.value })}
                    className="w-20 rounded-xl border-[#E8E2D9] text-center" />
                  <span className="text-sm text-ink-muted">min</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => setRestOpen(false)} className="rounded-full border-[#E8E2D9]">Annuleren</Button>
            <Button type="button" onClick={applyRest} disabled={restSaving} data-testid="apply-rest-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {restSaving ? "Toepassen…" : "Toepassen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Button onClick={save} data-testid="save-settings-btn" className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white h-12 px-8">
        <Save className="w-4 h-4 mr-2" /> Opslaan
      </Button>
    </div>
  );
}
